import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { 
  Package, Clock, MapPin, Phone, CheckCircle, Truck, 
  ChefHat, ArrowLeft, MessageCircle, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChatWindow } from '../components/ChatWindow';
import { toast } from 'sonner';

export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { formatPrice } = useCurrency();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Order not found');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading || !order) {
    return (
      <div className="min-h-screen bg-slate-50 pt-24">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2 mb-8"></div>
            <div className="bg-white rounded-2xl p-6">
              <div className="h-6 bg-slate-200 rounded w-1/4 mb-4"></div>
              <div className="h-20 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusSteps = [
    { key: 'confirmed', label: 'Confirmed', Icon: CheckCircle },
    { key: 'preparing', label: 'Preparing', Icon: ChefHat },
    { key: 'ready', label: 'Ready', Icon: Package },
    { key: 'in_transit', label: 'On the Way', Icon: Truck },
    { key: 'delivered', label: 'Delivered', Icon: CheckCircle },
  ];

  const currentStep = statusSteps.findIndex(s => s.key === order.status);

  const renderOrderItems = () => {
    const items = [];
    for (let i = 0; i < order.items.length; i++) {
      const item = order.items[i];
      items.push(
        <div key={i} className="flex justify-between text-sm">
          <span className="text-slate-600">{item.quantity}x {item.name}</span>
          <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
        </div>
      );
    }
    return items;
  };

  const renderStatusSteps = () => {
    const steps = [];
    for (let i = 0; i < statusSteps.length; i++) {
      const step = statusSteps[i];
      const Icon = step.Icon;
      const isActive = i <= currentStep;
      const isCurrent = i === currentStep;
      
      steps.push(
        <div key={step.key} className="flex flex-col items-center flex-1">
          <div className="relative">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isActive ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-400'
            } ${isCurrent ? 'ring-4 ring-orange-100' : ''}`}>
              <Icon className="w-5 h-5" />
            </div>
          </div>
          <span className={`mt-2 text-xs font-medium ${
            isActive ? 'text-orange-500' : 'text-slate-400'
          }`}>
            {step.label}
          </span>
        </div>
      );
    }
    return steps;
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            className="rounded-full"
            onClick={() => navigate('/orders')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Order #{order.id.slice(0, 8).toUpperCase()}
            </h1>
            <p className="text-slate-500">{formatDate(order.created_at)}</p>
          </div>
        </div>

        {order.status !== 'cancelled' && order.status !== 'pending' && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Order Status</h2>
              {['in_transit', 'picked_up', 'preparing', 'ready'].includes(order.status) && (
                <Link to={`/order/${order.id}/track`}>
                  <Button className="btn-primary" size="sm" data-testid="track-order-button">
                    <MapPin className="w-4 h-4 mr-2" />
                    Track Live
                  </Button>
                </Link>
              )}
            </div>
            <div className="flex justify-between">
              {renderStatusSteps()}
            </div>
          </div>
        )}

        {order.status === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
            <p className="text-red-700 font-medium">This order has been cancelled</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Details</h2>
            
            <div className="mb-4">
              <h3 className="font-medium text-slate-900">{order.restaurant_name}</h3>
            </div>

            <div className="space-y-3">
              {renderOrderItems()}
            </div>

            <div className="border-t border-slate-100 mt-4 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Delivery Fee</span>
                <span>{formatPrice(order.delivery_fee)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-100">
                <span>Total</span>
                <span className="text-orange-500">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Delivery Information</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Delivery Address</p>
                  <p className="font-medium text-slate-900">
                    {order.delivery_address}, {order.delivery_city}, {order.delivery_postal_code}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Contact</p>
                  <p className="font-medium text-slate-900">{order.delivery_phone}</p>
                </div>
              </div>

              {order.delivery_instructions && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-500">Instructions</p>
                  <p className="text-slate-700">{order.delivery_instructions}</p>
                </div>
              )}
            </div>

            <Button 
              className="w-full mt-6"
              variant="outline"
              onClick={() => setShowChat(!showChat)}
              data-testid="toggle-chat"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {showChat ? 'Hide Chat' : 'Contact Support'}
            </Button>
          </div>
        </div>

        {/* Real-time Chat Window */}
        <ChatWindow
          orderId={order.id}
          isOpen={showChat}
          onClose={() => setShowChat(false)}
          minimized={chatMinimized}
          onToggleMinimize={() => setChatMinimized(!chatMinimized)}
        />
      </div>
    </div>
  );
}
